import styles from './index.module.css'

import { LuX } from '@react-icons/all-files/lu/LuX'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { EMAIL } from '@disco/constants/RecipientTypes'
import { referralInvite } from '@disco/state-management/actions/ReferralInviteActions'
import { getUser } from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'
import { validateEmail } from '@disco/utils/validation'

import type { Recipient } from 'types/list'

import {
  Badge,
  Box,
  Button,
  Dialog,
  Flex,
  IconButton,
  Link,
  Text,
  TextArea,
  TextField,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

const ERROR_SENDING_INVITE =
  'There was an error trying to send an invite. Our team has been notified.'

interface RecipientTag {
  id: string
  email: string
}

interface Props {
  onClose: () => void
}

const ReferralInviteSendModal: React.FC<Props> = ({ onClose }) => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)

  const [recipients, setRecipients] = useState<RecipientTag[]>([])
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [sentCount, setSentCount] = useState<number | null>(null)

  useEffect(() => {
    mixpanel.sendPureEvent('Open referral modal')
  }, [])

  const referralUrl = useMemo(() => {
    const params = new URLSearchParams({
      b: String(user.business?.id ?? ''),
      u: String(user.id),
    })
    return `https://disco.ac/signup?${params.toString()}`
  }, [user])

  const addRecipient = useCallback(() => {
    const trimmed = email.trim()

    if (!validateEmail(trimmed)) {
      setError('Please enter a valid email address')
      return
    }

    const isDuplicate = recipients.some(
      r => r.email.toLowerCase() === trimmed.toLowerCase(),
    )
    if (isDuplicate) {
      setError('Enter a unique email')
      return
    }

    setError(null)
    setRecipients(prev => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, email: trimmed },
    ])
    setEmail('')
  }, [email, recipients])

  const removeRecipient = useCallback((id: string) => {
    setRecipients(prev => prev.filter(r => r.id !== id))
  }, [])

  const handleEmailKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        addRecipient()
      }
    },
    [addRecipient],
  )

  const handleCopyLink = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault()
      try {
        await navigator.clipboard.writeText(referralUrl)
        mixpanel.sendPureEvent('Copy public referral link')
        pushUINotification({ message: 'Copied to clipboard', level: 'success' })
      } catch {
        pushUINotification({
          message: 'Could not copy the link. Copy it manually.',
          level: 'error',
        })
      }
    },
    [referralUrl],
  )

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (!recipients.length || isSending) return

      const payload: Recipient[] = recipients.map(r => ({
        email: r.email,
        type: EMAIL,
        name: '',
      })) as Recipient[]

      setError(null)
      setIsSending(true)
      dispatch(referralInvite(payload, message.trim()))
        .then(result => {
          // the thunk catches API errors and resolves with the Error object
          if (result && !(result instanceof Error)) {
            setSentCount(payload.length)
          } else {
            setError(ERROR_SENDING_INVITE)
          }
        })
        .catch(() => setError(ERROR_SENDING_INVITE))
        .finally(() => setIsSending(false))
    },
    [recipients, message, isSending, dispatch],
  )

  const handleInviteMore = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setRecipients([])
    setEmail('')
    setMessage('')
    setError(null)
    setSentCount(null)
  }, [])

  const sendButtonCaption = recipients.length
    ? `Send invite to ${recipients.length} ${
        recipients.length > 1 ? 'friends' : 'friend'
      }`
    : 'Send invite to friends'

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Dialog.Content maxWidth="440px" aria-describedby={undefined}>
        <Flex justify="between" align="start" mb="1">
          <Dialog.Title size="5" mb="0">
            {sentCount ? 'Thank you!' : 'Give the gift of DISCO'}
          </Dialog.Title>
          <IconButton
            variant="ghost"
            color="gray"
            size="2"
            aria-label="Close"
            onClick={onClose}
          >
            <LuX size={18} />
          </IconButton>
        </Flex>
        {sentCount ? (
          <>
            <Text size="2" color="gray" as="p">
              {`Thanks so much for helping us spread the word. We’ll let you know when your ${
                sentCount > 1 ? 'friends sign up' : 'friend signs up'
              }.`}
            </Text>
            <Box mt="5">
              <Button
                size="3"
                variant="solid"
                color="gray"
                highContrast
                className={styles.sendButton}
                onClick={onClose}
              >
                Done
              </Button>
            </Box>
            <Text size="2" align="center" as="div" mt="3">
              <Link href="#" underline="always" onClick={handleInviteMore}>
                Invite more friends
              </Link>
            </Text>
          </>
        ) : (
          <>
            <Text size="2" color="gray" as="p">
              Invite friends. When they subscribe, you both get credit.
            </Text>

            <Flex gap="3" mt="4">
              <Box className={styles.creditCard}>
                <Text size="5" weight="bold" as="div">
                  $20
                </Text>
                <Text size="2" as="div">
                  Credit for you
                </Text>
              </Box>
              <Box className={styles.creditCard}>
                <Text size="5" weight="bold" as="div">
                  $20
                </Text>
                <Text size="2" as="div">
                  Credit per friend
                </Text>
              </Box>
            </Flex>

            <form onSubmit={handleSubmit}>
              <Box mt="4">
                <Text size="2" as="label" htmlFor="referral-email">
                  Invite by email
                </Text>
                <Flex gap="2" mt="1">
                  <Box className={styles.emailInput}>
                    <TextField.Root
                      id="referral-email"
                      size="2"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={handleEmailKeyDown}
                    />
                  </Box>
                  <Button
                    type="button"
                    size="2"
                    variant="surface"
                    color="gray"
                    disabled={!email.trim()}
                    onClick={addRecipient}
                  >
                    Add
                  </Button>
                </Flex>
                {recipients.length > 0 && (
                  <Flex gap="2" wrap="wrap" mt="2">
                    {recipients.map(recipient => (
                      <Badge
                        key={recipient.id}
                        size="2"
                        variant="soft"
                        color="gray"
                        radius="full"
                      >
                        {recipient.email}
                        <IconButton
                          type="button"
                          variant="ghost"
                          color="gray"
                          size="1"
                          radius="full"
                          aria-label={`Remove ${recipient.email}`}
                          onClick={() => removeRecipient(recipient.id)}
                        >
                          <LuX size={12} />
                        </IconButton>
                      </Badge>
                    ))}
                  </Flex>
                )}
                {error && (
                  <Text size="2" color="red" mt="2" as="div">
                    {error}
                  </Text>
                )}
              </Box>

              <Box mt="4">
                <Text size="2" as="label" htmlFor="referral-message">
                  Add a message (optional)
                </Text>
                <Box mt="1">
                  <TextArea
                    id="referral-message"
                    size="2"
                    rows={4}
                    placeholder="Thought you’d like this…"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  />
                </Box>
              </Box>

              <Box mt="5">
                <Button
                  type="submit"
                  size="3"
                  variant="solid"
                  color="gray"
                  highContrast
                  className={styles.sendButton}
                  disabled={!recipients.length}
                  loading={isSending}
                >
                  {sendButtonCaption}
                </Button>
              </Box>
            </form>

            <Text size="2" align="center" as="div" mt="3">
              Or{' '}
              <Link href="#" underline="always" onClick={handleCopyLink}>
                copy your referral link
              </Link>
            </Text>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default ReferralInviteSendModal
